/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import type { LoadedRecommendationBlock } from '@/types/RecommendationBlocks'
import { debug } from '@/services/Log'
import { handleTildedField } from '@/helpers/handleTildedField'
import { addParamsToUrl } from '@/helpers/addParamsToUrl'
import { sendDMPAnalyticsEvent } from '@/hooks/DmpAnalytics/pushDMPAnalyticsEvent'

const Carousel = dynamic(import('@/components/Generic/Carousel'))
const ProductTile = dynamic(import('@/components/dls/productTiles/ProductTile'))
const Text = dynamic(import('@csc/dls/Text'))

const listrakImageStyleMap = {
  mobile: {
    minWidth: '144px',
    minHeight: '144px',
  },
  desktop: {
    minWidth: '264px',
    minHeight: '264px',
  },
}
const dedupSkus = <T extends { sku: string }>(arr: readonly T[]): readonly T[] => {
  const seen = new Set<string>()
  return arr.filter((item) => {
    if (seen.has(item.sku)) return false
    seen.add(item.sku)
    return true
  })
}
const ListrackRecommendationUi: React.FC<{
  isMobile: boolean,
  block: LoadedRecommendationBlock,
  isFreeRugDoorstepShippingOn: boolean,
  addToCartFunction?: (d: { sku: string }) => void,
}> = ({
  isMobile,
  block,
  isFreeRugDoorstepShippingOn,
  addToCartFunction,
}) => (
  <Carousel
    className="overflow-hidden"
    carrouselHeader={<Text textId="text-body-xl-bold px-2">{block.carrouselHeader}</Text>}
    isMobile={isMobile}
  >
    {dedupSkus(block.products).map((product, listrakRecommendationIndex) => (
      <div
        key={`${block.mechandiseBlockId}-${product.sku}`}
        style={listrakImageStyleMap[isMobile ? 'mobile' : 'desktop']}
        className="mx-2 relative"
      >
        <ProductTile
          addToCartFunction={addToCartFunction}
          onImpressionEvent={() => {
            sendDMPAnalyticsEvent({
              eventType: 'recommendation-impression',
              items: [{
                salePrice: product.variants?.[0]?.sale_price,
                price: product.variants?.[0]?.price,
                sku: product.sku,
                quantity: 1,
              }],
              context: [{
                key: 'mechandiseBlockId',
                value: {
                  stringValue: block.mechandiseBlockId,
                  numberValue: 0,
                  booleanValue: false,
                },
              }, {
                key: 'mechandiseBlockName',
                value: {
                  stringValue: block.name,
                  numberValue: 0,
                  booleanValue: false,
                },
              }, {
                key: 'RecommendationIndex',
                value: {
                  stringValue: String(listrakRecommendationIndex),
                  numberValue: listrakRecommendationIndex,
                  booleanValue: false,
                },
              }],
            })
          }}
          product={{
            ...product,
            colorChips: undefined,
            readyToShip: false,
            brand: '',
            custom_url: {
              url: addParamsToUrl(product.custom_url.url, {
                listrakMechandiseBlockId: block.mechandiseBlockId,
                listrakRecommendationIndex: String(listrakRecommendationIndex),
                listrakRecommendationBlockName: block.name,
                sku: product.variants?.[0]?.sku,
              }),
            },
            FreeShipping: (
              product.FreeShipping
              || (
                isFreeRugDoorstepShippingOn
                && product.hasDoorstepEligibleRug
              )
            ),
            is_free_shipping_eligible: (
              isFreeRugDoorstepShippingOn
              && !product.hasDoorstepEligibleRug
              && product.allVariantsAreDSElligibleRugs
            ),
          }}
          lazyLoad
          changeImageOnHover
          capSize
        />
      </div>
    ))}
  </Carousel>
)

const ListrakRecommendation: React.FC<{
  isMobile: boolean,
  block: LoadedRecommendationBlock,
  index: number,
  isFreeRugDoorstepShippingOn: boolean,
  addToCartFunction?: (d: { sku: string }) => void,
}> = ({
  isMobile,
  block,
  index,
  isFreeRugDoorstepShippingOn,
  addToCartFunction,
}) => {
  useEffect(() => {
    debug('Listrak recommendation view_item', { block })
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ ecommerce: null }) // Clear the previous ecommerce object.
    window.dataLayer.push({
      // event: 'view_recommendation_list',
      event: 'view_item_list',
      ecommerce: {
        recommendation: {
          item_list_name: block.name,
          item_list_id: block.mechandiseBlockId,
          rank: String(index),
          items: block.products.map((product) => ({
            item_id: product.sku,
            item_name: handleTildedField(product.name),
          })),
        },
      },
    })
  }, [block, index])
  return (
    <ListrackRecommendationUi
      addToCartFunction={addToCartFunction}
      isFreeRugDoorstepShippingOn={isFreeRugDoorstepShippingOn}
      isMobile={isMobile}
      block={block}
    />
  )
}

export default ListrakRecommendation
